import { UseFormReturn } from "react-hook-form";

import {
  AttachProductFormData,
  ChildProductFormData,
  CustomProductOption,
  CustomProductSuggestion,
  CustomProductSuggestionPost,
  DuplicateProductRes,
  LoadProductParams,
  Product,
  ProductFormData,
  ProductImport,
  ProductImportCatAssociateImport,
  ProductImportRst,
  ProductInventory,
  ProductList,
  ProductSearchRst2,
  TopUpSuggestion,
  UpdateProductRes,
  UpdateProductRes422,
} from "../types/product";
import { get, post, postDontHandleError } from "./fetch.core";
import { initPromise } from "./init.core";

export async function loadProducts(params: Partial<LoadProductParams>) {
  if (!params.search) {
    delete params.search;
  }

  if (!params.categoryIds || params.categoryIds.length === 0) {
    delete params.categoryIds;
  }

  if (!params.tagId || params.tagId.length === 0) {
    delete params.tagId;
  }

  if (!params.attributesIds || params.attributesIds.length === 0) {
    delete params.attributesIds;
  }

  if (!params.brandIds || params.brandIds.length === 0) {
    delete params.brandIds;
  }

  if (params.isPromotionProduct === undefined || params.isPromotionProduct === null || params.isPromotionProduct === false) {
    delete params.isPromotionProduct;
  }

  if (!params.statuses || params.statuses.length === 0) {
    delete params.statuses;
  }

  if (!params.productTypes || params.productTypes.length === 0) {
    delete params.productTypes;
  }

  if (params.priceMin || params.priceMax) {
    if (!params.priceMin) {
      params.priceMin = -9999999;
    }
    if (!params.priceMax) {
      params.priceMax = 9999999;
    }
  } else {
    delete params.priceMin;
    delete params.priceMax;
  }

  if (params.rewardPayoutMin || params.rewardPayoutMax) {
    if (!params.rewardPayoutMin) {
      params.rewardPayoutMin = -9999999;
    }
    if (!params.rewardPayoutMax) {
      params.rewardPayoutMax = 9999999;
    }
  } else {
    delete params.rewardPayoutMin;
    delete params.rewardPayoutMax;
  }

  return (await post<ProductList>("/admin/product/list", params)).data;
}

export async function loadProductbyTag(id: string) {
  return (await post<ProductList>("/admin/product/listtag", { id })).data;
}

export async function getProduct(productId: string) {
  await initPromise;
  return (await get<Product>(`/admin/product/get?productId=${productId}`)).data;
}

export async function getProductBasicInfo(productId: string) {
  await initPromise;
  return (await get<{ id: string; sku: string }>(`/admin/product/getBasicInfo?productId=${productId}`)).data;
}

export async function getCustomOptions(productId: string) {
  return (await get<CustomProductOption[]>(`/admin/product/getCustomOptions?productId=${productId}`)).data;
}

export async function updateCustomOptions(data: { productId: string; customOptions: CustomProductOption[] }) {
  return (await post<CustomProductOption[]>(`/admin/product/updateCustomOptions`, data)).data;
}

export async function getCustomSuggestions(productId: string) {
  return (await get<CustomProductSuggestion[]>(`/admin/product/getCustomSuggestions?productId=${productId}`)).data;
}

export async function updateCustomSuggestions(data: { productId: string; customSuggestions: CustomProductSuggestionPost[] }) {
  return (await post<CustomProductSuggestion[]>(`/admin/product/updateCustomSuggestions`, data)).data;
}

export async function getTopUpSuggestions(productId: string) {
  return (await get<TopUpSuggestion[]>(`/admin/product/getTopUpSuggestions?productId=${productId}`)).data;
}

export async function updateTopUpSuggestions(data: { productId: string; topUpSuggestions: TopUpSuggestion[] }) {
  return (await post<TopUpSuggestion[]>(`/admin/product/updateTopUpSuggestions`, data)).data;
}

function sanitizeFormData(data: Partial<ProductFormData>) {
  if ((data.categoryIds as any) === false) data.categoryIds = [];
  if ((data.rewardPayout as any) === "") delete data.rewardPayout;
  if ((data.price as any) === "") (data as any).price = null;
  if ((data.brandId as any) === "") delete data.brandId;
  if ((data.shelfId as any) === "") delete data.shelfId;
  if ((data.eshopPrice as any) === "") (data as any).eshopPrice = null;
  if ((data.discountPrice as any) === "") (data as any).discountPrice = null;
  if ((data.defaultRetailPrice as any) === "") (data as any).defaultRetailPrice = null;
  data.retailPrices = (data as any).retailPrices.map((rs) => ({
    retailShopId: rs.retailShopId,
    price: rs.price === "" ? null : rs.price,
  }));
  if ((data.recycleDeposit as any) == "") (data as any).recycleDeposit = null;
  if ((data.recycleInventoryProduct as any) == "") (data as any).recycleInventoryProduct = null;
  if ((data.barcode as any) === "") (data as any).barcode = null;
  if (!data.excludeShipOptionIds) data.excludeShipOptionIds = [];
  if ((data.cost as any) === "") delete data.cost;

  if (data.boxConversion && (data.boxConversion.conversionRate as any) === "") {
    delete data.boxConversion;
  }

  return data;
}

export async function createProduct(data: ProductFormData) {
  return await postDontHandleError<DuplicateProductRes>("/admin/product/create", sanitizeFormData(data));
}

export async function updateProduct(productId: string, data: ProductFormData) {
  const updateData = { ...sanitizeFormData(data), productId };
  const res = await postDontHandleError<DuplicateProductRes | UpdateProductRes | UpdateProductRes422>(
    "/admin/product/update",
    updateData
  );

  return res;
}

export async function importProducts(productImports: ProductImport[]) {
  const rst = await post<ProductImportRst[]>("/admin/product/updateOrImport", { products: productImports });
  return rst.data;
}

export async function importProductCatAssociate(productImports: ProductImportCatAssociateImport[]) {
  const rst = await post<ProductImportRst[]>("/admin/product/importCatAssociate", { products: productImports });
  return rst.data;
}

async function createChildProduct(data: ChildProductFormData, parentProduct: Product) {
  const d = sanitizeChildFormData(data, parentProduct);
  const res = await postDontHandleError<DuplicateProductRes>("/admin/product/create", d);
  return res;
}

async function updateChildProduct(data: ChildProductFormData, parentProduct: Product, childProductId: string) {
  const updateData = { ...sanitizeChildFormData(data, parentProduct), productId: childProductId };
  const res = await postDontHandleError<DuplicateProductRes | UpdateProductRes | UpdateProductRes422>(
    "/admin/product/update",
    updateData
  );
  return res;
}

async function deleteProduct(productId: string) {
  const res = await post<{ message: string }>("/admin/product/delete", { productId });
  return res.data;
}

async function updateProductCost(productId: string, cost: number) {
  const res = await post<{ message: string }>("/admin/product/updateCost", { productId, cost });
  return res.data;
}

export async function attachProduct(data: AttachProductFormData) {
  await post("/admin/product/attachProduct", data);
}

export async function reOrderVariations(parentId: string, variationIds: string[]) {
  return await post("/admin/product/reOrderVariations", {
    parentId,
    variationIds,
  });
}

export async function updateProductPrice(data: {
  productId: string;
  price?: number;
  eshopPrice?: number;
  discountPrice?: number;
  defaultRetailPrice?: number;
  retailPrice?: { retailShopId: string; price: number };
  membershipPrice?: { membershipId: string; price: number };
}) {
  return await post("/admin/product/updatePrice", data);
}

export function convertProductToForm(product: Product): ProductFormData {
  return {
    name: product.name,
    description: product.description,
    tagId: product.tag === undefined ? [] : product.tag.map((c) => c.id),
    shortDescription: product.shortDescription,
    price: product.price,
    eshopPrice: product.eshopPrice,
    discountPrice: product.discountPrice,
    defaultRetailPrice: product.defaultRetailPrice,
    retailPrices: product.retailPrices || [],
    membershipPrices: product.membershipPrices || [],
    recycleDeposit: product.recycleDeposit,
    recycleInventoryProduct: product.recycleInventoryProduct,
    galleryIds: product.galleries === undefined ? [] : product.galleries.map((g) => g.id),
    categoryIds: product.categories === undefined ? [] : product.categories.map((c) => c.id),
    featuredImgId: product.featureMedia === undefined ? undefined : product.featureMedia.id,
    descriptionGalleryIds: product.descriptionGalleries === undefined ? [] : product.descriptionGalleries.map((c) => c.id),
    sku: product.sku,
    brandId: product.brand?.id ?? "",
    shelfId: product.shelf?.id ?? "",
    isPromotionProduct: product.isPromotionProduct,
    productType: product.productType,
    attributes: product.attributes,
    rewardPayout: product.rewardPayout,
    isRewardRedeemable: product.isRewardRedeemable,
    relatedProductIds: product.relatedProducts === undefined ? [] : product.relatedProducts.map((p) => p.id),
    availability: product.availability,
    stockReadyDate: product.stockReadyDate,
    expiaryDate: product.expiaryDate,
    attachProducts: product.attachProducts,
    status: product.status,
    extUrl: product.extUrl,
    barcode: product.barcode,
    excludePlacement: product.excludePlacement,
    excludePlacementLocation: product.excludePlacementLocation,
    excludePlacementRetailIds: product.excludePlacementRetailIds,
    isPreOrder: product.isPreOrder,
    preOrderLimit: product.preOrderLimit,
    preOrderEstArrival: product.preOrderEstArrival,
    preOrderRemarks: product.preOrderRemarks,
    boxConversion: product.boxConversion,
    excludeShipOptionIds: product.excludeShipOptionIds,
    cost: product.cost,
    bundleProducts: product.bundleProducts,
  };
}

function sanitizeChildFormData(data: ChildProductFormData, parentProduct: Product) {
  // if (localeCore.isEmpty(data.name)) delete data.name;
  // if (localeCore.isEmpty(data.description)) delete data.description;
  if ((data.barcode as any) === "") (data as any).barcode = null;
  if (data.boxConversion) {
    if (!data.boxConversion.conversionRate && !data.boxConversion.productId) delete data.boxConversion;
  }
  return {
    productType: "variationChild",
    parentId: parentProduct.id,
    price: data.price,
    eshopPrice: data.eshopPrice,
    defaultRetailPrice: data.defaultRetailPrice,
    retailPrices: data.retailPrices,
    membershipPrices: data.membershipPrices,
    recycleDeposit: data.recycleDeposit,
    name: data.name,
    galleryIds: data.galleryIds,
    categoryIds: data.categoryIds,
    featuredImgId: data.featuredImgId,
    descriptionGalleryIds: data.descriptionGalleryIds,
    sku: data.sku,
    availability: data.availability,
    barcode: data.barcode,
    boxConversion: data.boxConversion,

    // stockReadyDate: data.stockReadyDate === "" ? undefined : data.stockReadyDate,
    attributes: data.attributes.map((a) => {
      return {
        attributeId: a.attributeId,
        value: a.value,
      };
    }),
  };
}

async function searchProduct(search: string, withInventories = false, directlyShowChildProducts = false) {
  const res = await post<ProductSearchRst2>("/admin/product/list", {
    search,
    withInventories,
    directlyShowChildProducts,
  });
  return res.data;
}

function setDuplicateError(methods: UseFormReturn<any, any>, resData: DuplicateProductRes) {
  const skuDuplicate = resData.errors.find((e) => e.source === "sku");
  const barCodeDuplicate = resData.errors.find((e) => e.source === "barcode");
  if (skuDuplicate) methods.setError("sku", { message: "Duplicate sku found" });
  if (barCodeDuplicate) {
    const href =
      barCodeDuplicate.product.productType === "variationChild"
        ? `/product/child-edit?variationId=${barCodeDuplicate.product.id}&parentId=${barCodeDuplicate.product.parentId}`
        : `/product/edit/${barCodeDuplicate.product.id}`;

    methods.setError("barcode", {
      message: `<p>A product <a href='${href}' style="color:blue; text-decoration:underline">(${barCodeDuplicate.product.name})</a> with barcode already exists</p>`,
    });
  }
}

async function getSingleProductInventory(productId: string) {
  const res = await post<ProductInventory>(`/admin/product/getSingleProductInventory?productId=${productId}`, {});
  return res.data;
}

async function saveproductDescriptionDraft(productId: string, key: string, descriptionjson: string, descriptionhtml: string) {
  const res = await post(`/admin/product/updateDescriptionDraft`, {
    productId,
    key,
    descriptionjson,
    descriptionhtml,
  });
  return res.data;
}

export async function batchAddCategories(productIds: string[], categoryIds: string[]) {
  return await post("/admin/product/batchAddCategories", {
    productIds,
    categoryIds,
  });
}

export async function batchRemoveCategories(productIds: string[], categoryIds: string[]) {
  return await post("/admin/product/batchRemoveCategories", {
    productIds,
    categoryIds,
  });
}

export async function batchAddTags(productIds: string[], tagIds: string[]) {
  return await post("/admin/product/batchAddTags", {
    productIds,
    tagIds,
  });
}

export async function batchUpdatePrices(
  productIds: string[],
  data: {
    price?: number;
    eshopPrice?: number;
    defaultRetailPrice?: number;
    priceChange?: {
      type: "absolute" | "relative" | "percentage";
      value: number;
      fields: string[];
    };
  }
) {
  return await post("/admin/product/batchUpdatePrices", {
    productIds,
    ...data,
  });
}

export async function batchUpdateRetailPrices(
  productIds: string[],
  retailPrices: Array<{ retailShopId: string; price: string | null }>
) {
  return await post("/admin/product/batchUpdateRetailPrices", {
    productIds,
    retailPrices,
  });
}

export async function batchUpdateMembershipPrices(
  productIds: string[],
  membershipPrices: Array<{ membershipId: string; price: string | null }>
) {
  return await post("/admin/product/batchUpdateMembershipPrices", {
    productIds,
    membershipPrices,
  });
}

export async function batchUpdateCost(productIds: string[], cost: number) {
  return await post("/admin/product/batchUpdateCost", {
    productIds,
    cost,
  });
}

export async function batchUpdateStatus(productIds: string[], status: string) {
  return await post("/admin/product/batchUpdateStatus", {
    productIds,
    status,
  });
}

export async function batchUpdateAvailability(productIds: string[], availability: string) {
  return await post("/admin/product/batchUpdateAvailability", {
    productIds,
    availability,
  });
}

export async function batchUpdateBrand(productIds: string[], brandId: string) {
  return await post("/admin/product/batchUpdateBrand", {
    productIds,
    brandId,
  });
}

export async function batchAddAttributes(productIds: string[], attributes: Array<{ attributeId: string; value: any }>) {
  return await post("/admin/product/batchAddAttributes", {
    productIds,
    attributes,
  });
}

export async function batchRemoveAttributes(productIds: string[], attributes: Array<{ attributeId: string; value: any }>) {
  return await post("/admin/product/batchRemoveAttributes", {
    productIds,
    attributes,
  });
}

const productCore = {
  createChildProduct,
  updateChildProduct,
  searchProduct,
  deleteProduct,
  attachProduct,
  setDuplicateError,
  updateProductCost,
  getSingleProductInventory,
  saveproductDescriptionDraft,
  batchAddCategories,
  batchRemoveCategories,
  batchAddTags,
  batchUpdatePrices,
  batchUpdateRetailPrices,
  batchUpdateMembershipPrices,
  batchUpdateCost,
  batchUpdateStatus,
  batchUpdateAvailability,
  batchUpdateBrand,
  batchAddAttributes,
  batchRemoveAttributes,
};

export default productCore;
